import { useState } from "react";
import * as Sentry from "@sentry/react";
import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "@/common/lib/api";
import { bridgeStatusQueryKey, fetchBridgeStatus } from "@/common/lib/api/bridge";
import { isValidEzyvetBaseUrl, normalizeEzyvetBaseUrl } from "@/common/utils/ezyvetBaseUrl";
import { useAccountContext } from "@providers/AccountProvider";
import Spinner from "@/common/components/Spinner";
import ErrorBox from "@/common/components/ErrorBox";
import DownloadIcon from "@icons/download-01.svg?react";
import CheckCircleIcon from "@icons/check-circle.svg?react";
import BridgeStatus from "./bridge/BridgeStatus";
import EzyvetConnection from "./bridge/EzyvetConnection";
import EzyvetCredentialsUpload from "./bridge/EzyvetCredentialsUpload";
import EzyvetClinicPicker from "./bridge/EzyvetClinicPicker";
import EzyvetDoctorMerge from "./bridge/EzyvetDoctorMerge";

// ── Types ─────────────────────────────────────────────────────────────────────

type IntegrationSystem = "cornerstone" | "avimark" | "impromed" | "ezyvet";

interface IntegrationOption {
    id: IntegrationSystem;
    label: string;
    description: string;
    module: string; // the .dll filename for this integration, or how it ships when there isn't one
}

interface BridgeDownloadProps {
    /**
     * Whether to render the PIMS picker and the Download button, or only the status and
     * troubleshooting above them. Defaults to true so the beta/dev callers read unchanged; the
     * offer-cohort caller passes false.
     */
    showInstaller?: boolean;
}

interface BridgeDownloadUrlResponse {
    url: string; // GCS pre-signed URL — short-lived
    expires_at: string; // ISO 8601 — display to user
    version: string; // e.g. "0.1.0"
}

// ── Constants ─────────────────────────────────────────────────────────────────

const INTEGRATION_OPTIONS: IntegrationOption[] = [
    {
        id: "cornerstone",
        label: "Cornerstone",
        description: "Cornerstone Practice Management Software",
        module: "module_sybase.dll",
    },
    {
        id: "avimark",
        label: "AviMark",
        description: "AviMark Practice Management Software",
        module: "module_filestore.dll",
    },
    {
        id: "impromed",
        label: "Impromed",
        description: "Impromed Practice Management Software",
        module: "module_sqlserver.dll",
    },
    {
        id: "ezyvet",
        label: "ezyVet",
        description: "ezyVet Practice Management Software",
        module: "bundled in happydoc-bridge.exe (no separate module DLL)",
    },
];

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * BridgeDownload
 *
 * Renders inside the Integrations settings tab.
 * Lets the user pick their practice management system and download
 * the happy-bridge installer pre-bundled with the correct module.
 *
 * Flow:
 *   1. User selects their PIMS from the three options.
 *   2. User clicks Download — triggers GET /v1/bridge/download?integration={id}
 *   3. API returns a short-lived GCS pre-signed URL.
 *   4. Browser is redirected to the URL to start the download.
 */
interface BridgeInstallerProps {
    /**
     * The clinic's already-known ezyVet address (`BridgeStatus.ezyvet_base_url`), if any —
     * pre-fills the inline domain field once ezyVet is selected, instead of always rendering
     * empty on a re-download/reinstall when this account already has the address on file. Mirrors
     * `EzyvetConnection`'s own `currentBaseUrl` prop, which solves the identical problem for that
     * component's own address field.
     */
    currentEzyvetBaseUrl?: string | null;
    /** The PIMS a live bridge is already running. Pre-selected, and its button reads "ReDownload". */
    installedSystem?: IntegrationSystem | null;
}

/** The installer option a bridge `module_type` came from, or null for an unknown module. */
const MODULE_TYPE_SYSTEM: Record<string, IntegrationSystem> = {
    sybase: "cornerstone",
    sqlserver: "impromed",
    avimark: "avimark",
    filestore: "avimark",
    ezyvet: "ezyvet",
};

/**
 * The picker and the Download button.
 *
 * Its own component, and its own state, because it is gated separately from the status above it.
 * Download also fires `POST bridge/setup-integration`, which creates the happybridge integration;
 * it only goes live immediately when the account has no other integration, otherwise after the
 * bridge passes its checks (EN-1302). This fires on one click, with no confirmation and no
 * guided install. That is acceptable for the hand-held beta accounts and not for the rollout
 * cohort, whose route to the same setup is the wizard (`BridgeSetupFlow`), which walks the clinic
 * through the install and shows when the switch has happened.
 */
function BridgeInstaller({ currentEzyvetBaseUrl, installedSystem }: BridgeInstallerProps) {
    // Derived rather than seeded into state: bridge status loads after this mounts.
    const [picked, setSelected] = useState<IntegrationSystem | null>(null);
    const selected = picked ?? installedSystem ?? null;
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [downloadedVersion, setDownloadedVersion] = useState<string | null>(null);
    const [downloadedIntegration, setDownloadedIntegration] = useState<string | null>(null);
    // Non-fatal: bridge registration backfills the account_integrations row the first time the
    // bridge checks in (see the setup-integration catch below), so this never blocks the download —
    // it only tells the clinic the Sync button may not appear immediately, instead of leaving that
    // silent beyond a Sentry report (PR review, 2026-10-06).
    const [setupIntegrationFailed, setSetupIntegrationFailed] = useState(false);
    // ezyVet only: unlike Cornerstone/AVImark, whose bridge auto-detects its local DB connection,
    // there is no local artifact on a clinic's machine that reveals a cloud SaaS URL — a human has
    // to provide it. Saved BEFORE the download/install happens (see handleDownload), so the
    // bridge's very first config fetch already has the real address and no restart is needed
    // afterward (see EzyvetConnection, used for editing it again post-install). Initialized from
    // `currentEzyvetBaseUrl` so a re-download/reinstall doesn't make the clinic retype an address
    // this account already has on file.
    // Derived like `selected`: bridge status (and so `currentEzyvetBaseUrl`) loads after mount, and
    // an installed ezyVet bridge is pre-selected without a click, so a mount-time seed would be "".
    const [typedEzyvetBaseUrl, setEzyvetBaseUrl] = useState<string | null>(null);
    const ezyvetBaseUrl = typedEzyvetBaseUrl ?? currentEzyvetBaseUrl ?? "";
    // The exact (trimmed) address last known-saved -- either fetched via the latest bridge-status
    // query (currentEzyvetBaseUrl) or saved by THIS component during the current session -- or
    // null if neither has happened yet. Two jobs: (1) lets Download skip re-POSTing bridge/
    // ezyvet-connection outright when the field is untouched from what's already on file (PR
    // review, 2026-10-07 -- the previously-unconditional re-POST below raced a more recent edit
    // saved from a second tab via EzyvetConnection's own independent save path, with no conflict
    // warning); (2) lets a retry after a download/setup-integration failure skip re-POSTing an
    // address that already saved fine this session -- a changed input no longer matches, so it
    // still re-saves when it should.
    const [savedThisSession, setSavedEzyvetAddress] = useState<string | null>(null);
    const savedEzyvetAddress = savedThisSession ?? currentEzyvetBaseUrl ?? null;

    const trimmedEzyvetBaseUrl = ezyvetBaseUrl.trim();
    const isEzyvetMissingDomain = selected === "ezyvet" && trimmedEzyvetBaseUrl === "";
    // Only judged once something has actually been typed -- an empty field is "missing", not
    // "invalid", and the two get distinct messaging below.
    const isEzyvetInvalidDomain =
        selected === "ezyvet" &&
        trimmedEzyvetBaseUrl !== "" &&
        !isValidEzyvetBaseUrl(normalizeEzyvetBaseUrl(trimmedEzyvetBaseUrl));

    const handleDownload = async () => {
        if (!selected || isEzyvetMissingDomain || isEzyvetInvalidDomain) return;

        setIsLoading(true);
        setError(null);
        setSetupIntegrationFailed(false);

        const integrationLabel = INTEGRATION_OPTIONS.find((o) => o.id === selected)?.label ?? selected;
        // Read once up front, not re-checked after phase 1: `setSavedEzyvetAddress` below won't
        // have re-rendered yet by the time phase 2 runs in this same call, so re-reading
        // `savedEzyvetAddress` there would still see the OLD value anyway -- this is what phase 2
        // actually needs to know "was the address just saved in THIS click" rather than a prior one.
        const addressAlreadySaved =
            selected === "ezyvet" && savedEzyvetAddress === normalizeEzyvetBaseUrl(trimmedEzyvetBaseUrl);

        try {
            if (selected === "ezyvet" && !addressAlreadySaved) {
                // Must complete before download/setup-integration below: POST /bridge/register
                // (fired by the installer on first run) reads this staged value to seed the new
                // install's db_host, so the address has to be saved first.
                //
                // Skipped above (via addressAlreadySaved) only when the trimmed input already
                // matches a value THIS component itself knows was saved -- either from the latest
                // bridge-status query on mount, or from this component's own prior successful save
                // this session. Anything else (a changed input, or a retry where that memory is
                // stale) still re-POSTs: this is a cheap, idempotent upsert, and re-running it is
                // strictly safer than guessing it already landed.
                try {
                    await apiFetch("bridge/ezyvet-connection", {
                        method: "POST",
                        // Normalized here, not just validated against the normalized form (PR
                        // review, 2026-10-06) -- see EzyvetConnection's own save call for why.
                        json: { base_url: normalizeEzyvetBaseUrl(trimmedEzyvetBaseUrl) },
                        timeout: 15_000,
                    });
                    setSavedEzyvetAddress(normalizeEzyvetBaseUrl(trimmedEzyvetBaseUrl));
                } catch (err: any) {
                    Sentry.captureException(err, { tags: { bridgeDownloadPhase: "save_ezyvet_address" } });
                    let message = "Could not save your ezyVet address. Please check it and try again.";
                    const res = (err as { response?: { json: () => Promise<{ error?: string }> } })?.response;
                    if (res) {
                        try {
                            const body = await res.json();
                            if (body?.error) message = body.error;
                        } catch {
                            // Non-JSON error body — keep the generic message.
                        }
                    }
                    setError(message);
                    return;
                }
            }

            // Set up the integration record and fetch the download URL in parallel.
            // setup-integration creates the account_integrations row so the Sync
            // button and doctor mapping recognise this account once the bridge connects.
            const [data] = await Promise.all([
                apiFetch<BridgeDownloadUrlResponse>(`bridge/download`, {
                    method: "GET",
                    searchParams: { integration: selected },
                    timeout: 30_000,
                }),
                apiFetch(`bridge/setup-integration`, {
                    method: "POST",
                    json: { integration: selected },
                    timeout: 15_000,
                }).catch((err) => {
                    // Non-fatal — bridge registration will also create the row
                    Sentry.captureException(err, { tags: { bridgeDownloadPhase: "setup_integration" } });
                    setSetupIntegrationFailed(true);
                }),
            ]);

            if (!data?.url) {
                throw new Error("No download URL returned from server.");
            }

            // Redirect the browser to the pre-signed GCS URL.
            // The browser will start the download without navigating away.
            window.location.href = data.url;
            setDownloadedVersion(data.version ?? null);
            setDownloadedIntegration(integrationLabel);
        } catch (err: any) {
            Sentry.captureException(err, { tags: { bridgeDownloadPhase: "download" } });

            const isNetworkError =
                err?.message === "Failed to fetch" || err?.message === "Load failed" || err?.name === "TypeError";
            // Reaching this catch at all, for ezyVet, means phase 1 (saving the address) already
            // succeeded -- its own try/catch above returns early on failure, before this phase ever
            // runs. Say so: retrying only has to redo the download, not re-enter the address too.
            const addressNote = selected === "ezyvet" ? "Your ezyVet address was saved. " : "";
            setError(
                addressNote +
                    (isNetworkError
                        ? "Could not reach the server. Check your connection and try again, or contact support if the problem continues."
                        : "Failed to generate download link. Please try again or contact support."),
            );
        } finally {
            setIsLoading(false);
        }
    };

    const selectedOption = INTEGRATION_OPTIONS.find((o) => o.id === selected);

    return (
        <>
            {/* Pre-download guidance */}
            <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-800">
                <p className="font-medium mb-1">Before you download</p>
                <ul className="list-disc list-inside space-y-0.5 text-blue-700">
                    <li>Install on the computer that runs your PIMS server software</li>
                    <li>Requires Windows 10+ (64-bit), administrator access, and internet</li>
                </ul>
            </div>

            {/* System selector */}
            <div className="flex flex-col gap-2">
                <p className="text-sm font-medium text-gray-700">Select your practice management system</p>
                <div className="flex flex-col sm:flex-row gap-3">
                    {INTEGRATION_OPTIONS.map((option) => {
                        const isActive = selected === option.id;
                        return (
                            <button
                                key={option.id}
                                type="button"
                                onClick={() => {
                                    setSelected(option.id);
                                    setError(null);
                                    setDownloadedVersion(null);
                                    setDownloadedIntegration(null);
                                    // Drop any typed address; the field falls back to the known
                                    // address rather than blank when coming back to ezyVet.
                                    setEzyvetBaseUrl(null);
                                }}
                                className={[
                                    "flex flex-col items-start gap-1 rounded-lg border px-4 py-3 text-left transition-colors w-full sm:w-auto sm:flex-1 focus:outline-none focused",
                                    isActive
                                        ? "border-max-900 bg-max-50 text-max-900"
                                        : "border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50",
                                ].join(" ")}
                            >
                                <span className="text-sm font-semibold">{option.label}</span>
                                <span className={["text-xs", isActive ? "text-max-900" : "text-gray-500"].join(" ")}>
                                    {option.description}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* ezyVet address — required before Download is enabled. There is no local artifact
                on a clinic's machine the bridge can auto-detect a cloud SaaS URL from (unlike
                Cornerstone/AVImark's connection, which the bridge self-reports), so the address
                has to come from here, saved before the installer ever runs. */}
            {selected === "ezyvet" && (
                <div className="flex flex-col gap-2">
                    <label htmlFor="ezyvet-base-url" className="text-sm font-medium text-gray-700">
                        Your clinic&rsquo;s ezyVet address
                    </label>
                    <input
                        id="ezyvet-base-url"
                        type="text"
                        value={ezyvetBaseUrl}
                        onChange={(e) => {
                            setEzyvetBaseUrl(e.target.value);
                            setError(null);
                        }}
                        placeholder="https://yourclinic.ezyvet.com"
                        className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-max-500 sm:max-w-sm"
                    />
                    {isEzyvetInvalidDomain && (
                        <p className="text-xs text-red-600">
                            Must be an https:// URL on ezyvet.com or a *.ezyvet.com subdomain, e.g.
                            https://yourclinic.ezyvet.com
                        </p>
                    )}
                </div>
            )}

            {/* Download button + feedback */}
            <div className="flex flex-col gap-3">
                <div className="flex items-center gap-3">
                    {isLoading ? (
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                            <Spinner />
                            <span>Generating download link…</span>
                        </div>
                    ) : (
                        <button
                            type="button"
                            className="primary-button"
                            onClick={handleDownload}
                            disabled={!selected || isLoading || isEzyvetMissingDomain || isEzyvetInvalidDomain}
                        >
                            <DownloadIcon className="h-4 w-4" />
                            {selectedOption
                                ? `${selected === installedSystem ? "ReDownload" : "Download"} for ${selectedOption.label}`
                                : "Download"}
                        </button>
                    )}
                </div>

                {/* Success state + post-download instructions */}
                {downloadedVersion && !isLoading && (
                    <div className="flex flex-col gap-3">
                        <div className="flex items-center gap-2 text-sm text-green-700">
                            <CheckCircleIcon className="h-4 w-4 shrink-0" />
                            <span>
                                Download started — happy-bridge v{downloadedVersion} for{" "}
                                <span className="font-medium">{downloadedIntegration}</span>.
                            </span>
                        </div>
                        <ol className="list-decimal list-inside text-sm text-gray-600 space-y-1 ml-1">
                            <li>Run the downloaded installer as Administrator</li>
                            <li>Sign in with your HappyDoc account when prompted</li>
                            <li>The bridge will start automatically and appear in your system tray</li>
                            <li>Return here to verify connection status</li>
                        </ol>
                        <p className="text-xs text-gray-400">
                            Windows may show a security warning for new software. Click &ldquo;More info&rdquo; then
                            &ldquo;Run anyway&rdquo; to proceed.
                        </p>
                        {setupIntegrationFailed && (
                            <p className="text-xs text-amber-700">
                                Integration setup didn&rsquo;t complete immediately — this resolves automatically once
                                the bridge first checks in, so the Sync button may take a few minutes to appear.
                            </p>
                        )}
                    </div>
                )}

                {/* Error state */}
                {error && <ErrorBox msg={error} />}
            </div>

            {/* Requirements note */}
            <p className="text-xs text-gray-600">
                Requires Windows 10 or later (64-bit). An internet connection is needed during install and for
                operation. Contact support if you need help with deployment.
            </p>
        </>
    );
}

/**
 * BridgeDownload
 *
 * Renders inside the Integrations settings tab: bridge status and troubleshooting always, and the
 * installer (`BridgeInstaller`) only for callers that pass `showInstaller`.
 *
 * The split matters because the two halves are gated by different questions. The block is mounted
 * for an INSTALLED bridge so a live clinic keeps its status and troubleshooting whatever the
 * marketing flags say — but `installed: true` with a `setup_step` short of `done` is exactly the
 * resumable state the upgrade offer stays visible for, so a cohort clinic mid-migration used to get
 * the announcement card and the unguarded Download on the same page.
 */
export default function BridgeDownload({ showInstaller = true }: BridgeDownloadProps) {
    const { accountData } = useAccountContext();
    // Same cache entry `BridgeStatus` reads (see `bridgeStatusQueryKey`'s own doc comment) — this
    // is a second observer, not a second request. Needed here only to gate `EzyvetConnection`:
    // unlike Cornerstone/AVImark, whose bridge auto-detects its connection, ezyVet's address has
    // to be entered by a human, and there's nowhere to attach it until the ezyVet module itself
    // has registered (`module_type === "ezyvet"`).
    const { data: bridgeStatus } = useQuery({
        queryKey: bridgeStatusQueryKey(accountData?.id),
        queryFn: fetchBridgeStatus,
        staleTime: 60_000,
        refetchInterval: 5 * 60_000,
    });

    return (
        <div className="flex flex-col gap-6">
            {/* Header. The sub-line follows `showInstaller`, because the download sentence points at
                controls: "select your system below" and "download the correct version" are the PIMS
                picker and the Download button, and neither is on the page for a clinic that only gets
                status and troubleshooting. Describing absent controls to a clinic mid-migration reads
                as a broken page. */}
            <div>
                <h3 className="text-sm font-semibold text-gray-900">HappyDoc Bridge</h3>
                <p className="text-sm text-gray-500 mt-1">
                    {showInstaller ? (
                        <>
                            Download and install the HappyDoc Bridge application on the Windows computer that runs your
                            practice management software. Select your system below to download the correct version.
                        </>
                    ) : (
                        <>
                            HappyDoc Bridge runs on the Windows computer that runs your practice management software and
                            keeps the two connected.
                        </>
                    )}
                </p>
            </div>

            {/* Bridge status */}
            <BridgeStatus />

            {/* `is_active !== false` (not just `installed`): a retired installation still exists as
                a row, so GET bridge/status keeps reporting `installed: true` -- these controls read
                as "live and editable", which is wrong once BridgeStatus's own "deactivated, contact
                support" message is showing above. `!== false` (not `=== true`) matches that same
                component's own convention, treating a legacy row with no stamped value as active. */}
            {bridgeStatus?.installed && bridgeStatus.is_active !== false && bridgeStatus.module_type === "ezyvet" && (
                <>
                    <EzyvetConnection currentBaseUrl={bridgeStatus.ezyvet_base_url} />
                    <EzyvetCredentialsUpload />
                    <EzyvetClinicPicker />
                    <EzyvetDoctorMerge />
                </>
            )}

            {showInstaller && (
                <BridgeInstaller
                    currentEzyvetBaseUrl={bridgeStatus?.ezyvet_base_url}
                    installedSystem={
                        bridgeStatus?.installed && bridgeStatus.is_active !== false
                            ? (MODULE_TYPE_SYSTEM[bridgeStatus.module_type ?? ""] ?? null)
                            : null
                    }
                />
            )}
        </div>
    );
}
